import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { requestHandler } from '../utils/requestHandler';
import { mockAccountSummaryResponse } from '../mocks/mockAccountListResponse';
import {
  AppState,
  AccountListSlice,
  ApiEndPointErrorResponse,
} from '../interfaces/store.interface';
import { executeLogout } from './logoutSlice';
import { isMock, setErrorInAppState } from '../utils/helpers';
import {
  ACCOUNT_LIST_ENDPOINT,
  ACCOUNT_LIST_CONTEXT,
  X_SYF_ID_TOKEN,
  X_SYF_MERCHANT_ID,
} from '../utils/constants/apiConstants';
import {
  SESSION_ID_SESSION_STORAGE_KEY,
  CLIENT_ID_SESSION_STORAGE_KEY,
} from '../utils/constants/sessionStorageConstants';

export const initialState: AccountListSlice = {
  accounts: [],
  isAccountListLoading: false,
};

export const accountList = 'accountList';

export const fetchAccountList = createAsyncThunk(
  `${accountList}/fetchAccountList`,
  async (_arg, { getState }) => {
    const state: any = getState();
    const transmitToken = state.customerVerificationForm.transmitToken;
    const clientId = sessionStorage.getItem(CLIENT_ID_SESSION_STORAGE_KEY);
    const requestHeaders = {
      customHeaders: {
        [X_SYF_ID_TOKEN]: transmitToken,
        [X_SYF_MERCHANT_ID]: clientId,
      },
    };
    const requestPayload = {
      sessionId: sessionStorage.getItem(SESSION_ID_SESSION_STORAGE_KEY),
    };
    try {
      if (isMock()) {
        return mockAccountSummaryResponse;
      } else {
        const axiosInstance = requestHandler({
          ...requestHeaders,
        });
        const { data } = await axiosInstance.post(
          ACCOUNT_LIST_ENDPOINT,
          requestPayload,
        );
        return data;
      }
    } catch (error: any) {
      const errorObject = error?.response || error;
      setErrorInAppState(errorObject, ACCOUNT_LIST_CONTEXT);
      return error as ApiEndPointErrorResponse;
    }
  },
);

const addAccountDetailsFlag = (accounts = []): any => {
  if (accounts.length === 0) {
    return [];
  }
  return accounts.map((account: any) => {
    // add showAccountDetails property to incoming API accounts list data
    // set default to false to hide account Details by default for all accounts
    return { ...account, showAccountDetails: false };
  });
};

export const accountListSlice = createSlice({
  name: accountList,
  initialState,
  reducers: {
    renderAccountDetails: (state, { payload }) => {
      const accountFound = state.accounts.find(
        (singleAccount: any) => singleAccount.accountId === payload.accountId,
      );
      if (accountFound) {
        accountFound.showAccountDetails =
          payload.renderAccountDetailsDisplayFlag;
      }
    },

    updateCreditLimit: (state, { payload }) => {
      const { accountId, updatedAvailableBalance, newCreditLimit } = payload;
      const account = state.accounts.find(
        (selectedCliAccount: any) => selectedCliAccount.accountId === accountId,
      );
      if (account) {
        account.creditLimit = newCreditLimit;
        account.availableCredit = updatedAvailableBalance;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountList.pending, (state) => {
        state.isAccountListLoading = true;
        state.accounts = [];
      })
      .addCase(fetchAccountList.fulfilled, (state, { payload }: any) => {
        state.accounts = addAccountDetailsFlag(payload?.accounts);
        state.isAccountListLoading = false;
      })
      .addCase(fetchAccountList.rejected, (state) => {
        state.accounts = [];
        state.isAccountListLoading = false;
      })
      .addCase(executeLogout.fulfilled, (state) => {
        state.accounts = [];
      })
      .addCase(executeLogout.rejected, (state) => {
        state.accounts = [];
      })
      .addCase(executeLogout.pending, (state) => {
        state.accounts = [];
      });
  },
});

export const { renderAccountDetails, updateCreditLimit } =
  accountListSlice.actions;

export default accountListSlice.reducer;

// state getters can be written & exported here
export const getAccountData = (state: AppState) => state.accountList.accounts;
export const getAccountListLoading = (state: AppState) =>
  state.accountList.isAccountListLoading;
export const getSelectedShowAccountDetails = (
  state: AppState,
  selectedAccountId: string,
) => {
  if (state.accountList.accounts.length > 0) {
    const { showAccountDetails } = state.accountList.accounts.find(
      (account: any) => account.accountId === selectedAccountId,
    );
    return showAccountDetails;
  }
  return false;
};
